import type { FormFieldConfig, FormSectionConfig, BuildFieldsFromSettingsOptions } from '@/core/components'

// ─── Settings-driven form mapping (createYourAccount / shippingAddress) ──────
// TW checkout forms come from the FeelGreat settings response, mapped to core
// FormFieldConfig via buildFieldsFromSettings. These overrides align the BE
// field schema with the app's conventions:
//  - `street_address` → internal `address1` key.
//  - labels come straight from the settings (e.g. "Mobile Number" for shipping
//    phone, "Street address"); only `country` is forced (its BE label is empty).
//  - city stays a fixed dropdown (`select` + taiwanCityOptions), not the BE's
//    free-text field (product decision — 23 known TW cities).
//  - country is a disabled CountryBox defaulted to TW.
const taiwanFieldBaseOptions: BuildFieldsFromSettingsOptions = {
    keyMap: { street_address: 'address1' },
    // Taiwan uses a plain street-address field; city remains the fixed dropdown.
    typeByKey: { city: 'select', phone: 'phone', address1: 'text' },
    labelByKey: { country: 'country' },
    extraByKey: { country: { defaultValue: 'TW' } }
}

// Guest (create-account) = createYourAccount ∪ shippingAddress.
export const taiwanGuestFieldOptions: BuildFieldsFromSettingsOptions = {
    ...taiwanFieldBaseOptions,
    rowByKey: {
        firstname: 1,
        lastname: 1,
        email: 2,
        password: 2,
        phone: 3,
        country: 3,
        address1: 4,
        city: 5,
        zip_code: 5
    }
}

// Logged-in member = shippingAddress only (no password).
export const taiwanMemberFieldOptions: BuildFieldsFromSettingsOptions = {
    ...taiwanFieldBaseOptions,
    rowByKey: {
        firstname: 1,
        lastname: 1,
        email: 2,
        phone: 2,
        address1: 3,
        city: 3,
        zip_code: 4,
        country: 4
    }
}

// Fallbacks used only if the settings response hasn't provided form inputs
// (e.g. a settings race). Mirror the settings-driven layout above.
export const taiwanGuestFieldsFallback: FormFieldConfig[] = [
    { key: 'firstname', label: 'first_name', type: 'text', row: 1 },
    { key: 'lastname', label: 'last_name', type: 'text', row: 1 },
    { key: 'email', label: 'email_address', type: 'text', row: 2 },
    { key: 'password', label: 'password', type: 'password', row: 2 },
    { key: 'phone', label: 'phone', type: 'phone', row: 3 },
    { key: 'country', label: 'country', type: 'country_readonly', row: 3, defaultValue: 'TW' },
    { key: 'address1', label: 'street_address', type: 'text', row: 4 },
    { key: 'city', label: 'city', type: 'select', row: 5 },
    { key: 'zip_code', label: 'zip_code', type: 'text', row: 5 }
]

export const taiwanMemberFieldsFallback: FormFieldConfig[] = [
    { key: 'firstname', label: 'first_name', type: 'text', row: 1 },
    { key: 'lastname', label: 'last_name', type: 'text', row: 1 },
    { key: 'email', label: 'email_address', type: 'text', row: 2 },
    { key: 'phone', label: 'phone', type: 'phone', row: 2 },
    { key: 'address1', label: 'street_address', type: 'text', row: 3 },
    { key: 'city', label: 'city', type: 'select', row: 3 },
    { key: 'zip_code', label: 'zip_code', type: 'text', row: 4 },
    { key: 'country', label: 'country', type: 'country_readonly', row: 4, defaultValue: 'TW' }
]

// ─── Guest agreements (checkboxes below the form) ─────────────────────────────
export const guestAgreementFields: FormFieldConfig[] = [
    { key: 'pdpa', label: 'i_agree_unicity_member', type: 'checkbox', row: 1, defaultValue: false, linkText: 'terms_conditions_feelgreat' },
    { key: 'terms', label: 'form_signup_privacy_label', type: 'checkbox', row: 2, defaultValue: false }
]

// ─── Credit-card fields (same primitives as other countries) ─────────────────
export const creditCardFields: FormSectionConfig[] = [
    {
        title: 'new_credit_card',
        fields: [
            { key: 'name', label: 'name_on_card', type: 'text', row: 1 },
            { key: 'card_number', label: 'card_number', type: 'card_number', row: 1 },
            { key: 'exp_date', label: 'exp_date', type: 'exp_date', row: 2 },
            { key: 'cvv', label: 'cvv', type: 'cvv', row: 2 }
        ]
    }
]

export const taiwanConfig = {
    country: 'taiwan',
    code: '158',
    alpha2: 'TW',
    alpha3: 'TWN',
    currency: { code: 'TWD', symbol: '$' },
    dialCode: '+886',
    languages: ['en', 'zh'],
    defaultLanguage: 'en',
    countryListApiKey: 'country_twn',

    features: {
        auth: { enabled: true },
        products: { enabled: true },
        shipping: {
            enabled: true,
            // TW: delivery only (no pickup); address uses a fixed city dropdown.
            options: ['delivery'] as const,
            addressFormat: 'city-dropdown' as const
        },
        payments: {
            enabled: true,
            // Legacy TW gateway: direct Hydra order (credit card) + COD. No V3.
            methods: ['credit-card', 'cash-on-delivery'] as const,
            creditCardGateway: 'hydra',
            // hideCardActions in legacy → no saved-card/alias, no redirect.
            saveAlias: false,
            redirect: false
        },
        checkout: {
            taxInvoice: false,
            agreements: [] as const,
            recaptcha: true
        },
        // Product credit: some countries enable it, TW does. Gated per country;
        // implemented in-plugin for now (extract to core when a 2nd country enables it).
        productCredit: { enabled: true },
        // Standalone sign-up page disabled (legacy pageConfig.signUp.disable); only
        // in-checkout enrollment.
        signUp: { enabled: false },
        // Autoship/subscription disabled at checkout (legacy PaymentStep.autoship = -2).
        subscriptions: { enabled: false },
        orderHistory: { enabled: true }
    },

    // 20% VAT is defined but not displayed in the order summary (legacy: display:false).
    vat: { percentage: 20, description: 'vat', type: 'inclusive' as const, display: false },

    tracking: {
        ga4: '',
        fbPixel: ''
    },

    styles: {
        fontFamily: "'Inter', 'NotoSansThai', sans-serif",
        colors: {
            primary: '#003764',
            primaryLight: '#5A8FC3',
            heading: '#003764',
            text: '#003764',
            textMuted: 'rgb(11, 59, 97)',
            sectionHeading: 'rgb(21, 56, 98)',
            border: 'rgba(153, 188, 223, 0.25)',
            pageBg: 'rgb(209, 217, 226)',
            cardBg: '#ffffff'
        }
    }
} as const
